// SidebarHistory.jsx
/* ========================
 * SidebarHistory - Displays and manages the list of past conversations
 * ========================*/
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Search, ExternalLink, Trash2, Clock, X, Loader, EyeOff } from 'lucide-react';
import { HousePortal } from '@/App';
import { useConversation } from '@/managers/ConvoManager';
import { authManager } from '@/managers/AuthManager';
import { convertToDate, formatDateForSidebar } from '@/utils/timeUtils';
import ExpandedHistoryPanel from './ExpandedHistoryPanel';

const ChatHistoryItem = ({ chat, isActive, onClick, onDelete }) => {
    const timestamp = chat.lastUpdated || chat.clientSideCreated;
    const isIncognito = chat.privacyMode === 'incognito';

    return (
        <div
            className={`
                group flex items-center px-3 py-2 cursor-pointer
                hover:bg-surface-dark/60 transition-colors duration-200
                ${isActive ? 'bg-surface-dark/60' : ''}
            `}
            onClick={onClick}
        >
            <Clock size={14} className="text-on-surface-dim shrink-0 mr-2" />
            <span className="flex-1 text-xs text-on-surface truncate min-w-0">
                {chat.name || 'New Chat'}
            </span>
            {isIncognito && (
                <span className="inline-flex items-center gap-1 rounded-full border border-border-color px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-[0.08em] text-on-surface-dim">
                    <EyeOff size={10} /> Incognito
                </span>
            )}
            {timestamp && (
                <span className="text-[10px] text-on-surface-dim pt-1 mx-2 shrink-0">
                    {formatDateForSidebar(timestamp)}
                </span>
            )}
            <button
                onClick={(e) => {
                    e.stopPropagation();
                    onDelete(chat.id);
                }}
                className="p-1 opacity-0 group-hover:opacity-100
                         hover:bg-surface-dark/60 rounded-sm
                         transition-opacity duration-200 shrink-0"
            >
                <Trash2 size={14} className="text-on-surface-dim hover:text-red-400" />
            </button>
        </div>
    );
};

const SidebarHistory = ({ containerHeight, onClose }) => {
    const [searchHistoryTerm, setSearchHistoryTerm] = useState('');
    const [isExpandedHistoryOpen, setIsExpandedHistoryOpen] = useState(false);

    const {
        conversations: allConversationsMap,
        currentConversationId,
        switchToConversation,
        deleteConversation,
        isInitialized: isConvoManagerInitialized,
    } = useConversation();

    const handleSearchChange = (e) => {
        setSearchHistoryTerm(e.target.value);
    };

    const handleClearSearch = () => {
        setSearchHistoryTerm('');
    };
    
    const filteredConversations = useMemo(() => {
        const lowerCaseTerm = searchHistoryTerm.trim().toLowerCase();
        
        const conversationsArray = Object.values(allConversationsMap || {})
            .filter(c => c.id && (c.lastUpdated || c.clientSideCreated))
            .sort((a, b) => {
                const aDate = convertToDate(a.lastUpdated || a.clientSideCreated);
                const bDate = convertToDate(b.lastUpdated || b.clientSideCreated);
                return (bDate?.getTime() || 0) - (aDate?.getTime() || 0);
            });

        if (!lowerCaseTerm) {
            return conversationsArray;
        }

        return conversationsArray.filter(chat => 
            chat.name?.toLowerCase().includes(lowerCaseTerm)
        );
    }, [searchHistoryTerm, allConversationsMap]);

    const handleSwitchConversation = async (conversationId) => {
        try {
            await switchToConversation(conversationId);
            onClose?.(); 
        } catch (error) {
            console.error('Error switching conversation:', error);
        }
    };

    const handleDelete = async (chatId) => {
        if (window.confirm('Are you sure you want to delete this conversation?')) {
            await deleteConversation(chatId);
        }
    };
    
    const isHistoryListActuallyLoading = !isConvoManagerInitialized && Object.keys(allConversationsMap || {}).length === 0;

    return (
        <>
            <div
                className="flex flex-col border-t border-[var(--border-subtle)]"
                style={{ height: containerHeight }}
            >
                <div className="flex items-center justify-between p-3 border-b border-[var(--border-subtle)]">
                    <h2 className="text-xs font-semibold text-on-surface">Chat History</h2>
                    <button
                        className="p-1.5 hover:bg-surface-dark/60 rounded-md"
                        onClick={() => setIsExpandedHistoryOpen(true)}
                        title="Expand History"
                    >
                        <ExternalLink size={14} className="text-on-surface-dim" />
                    </button>
                </div>

                <div className="p-2 border-b border-[var(--border-subtle)]">
                    <div className="relative mb-2">
                        <input
                            type="text"
                            value={searchHistoryTerm}
                            onChange={handleSearchChange}
                            placeholder="Search by title..."
                            className="w-full bg-surface-dark text-on-surface pl-3 pr-8 py-1.5 rounded-md
                                     text-xs border border-[var(--border-subtle)] placeholder:text-on-surface-dim
                                     focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                        <div className="absolute right-2.5 top-1/2 transform -translate-y-1/2">
                            {searchHistoryTerm ? (
                                <button
                                    onClick={handleClearSearch}
                                    className="text-on-surface-dim hover:text-on-surface"
                                    title="Clear search"
                                >
                                    <X size={16} />
                                </button>
                            ) : (
                                <Search className="w-3.5 h-3.5 text-on-surface-dim" />
                            )}
                         </div>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto">
                    {isHistoryListActuallyLoading ? (
                        <div className="flex items-center justify-center h-[100px]">
                             <Loader size={14} className="animate-spin text-on-surface-dim mr-2" />
                            <span className="text-xs text-on-surface-dim">Loading history...</span>
                        </div>
                    ) : filteredConversations.length > 0 ? (
                        filteredConversations.map(chat => (
                            <ChatHistoryItem
                                key={chat.id}
                                chat={chat}
                                isActive={currentConversationId === chat.id}
                                onClick={() => handleSwitchConversation(chat.id)}
                                onDelete={handleDelete}
                            />
                        ))
                    ) : (
                         <div className="p-3 text-xs text-on-surface-dim text-center">
                            {searchHistoryTerm.trim()
                                ? 'No matching conversations found'
                                : 'No chat history found'}
                        </div>
                    )}
                </div>
            </div>

            {isExpandedHistoryOpen && (
                <HousePortal>
                    <ExpandedHistoryPanel
                        isOpen={isExpandedHistoryOpen}
                        onClose={() => setIsExpandedHistoryOpen(false)}
                    />
                </HousePortal>
            )}
        </>
    );
};

export default SidebarHistory;